import { call, put, takeLatest } from "redux-saga/effects";
import { AnyAction } from "redux";
import { startLoading, stopLoading, getUserDetails } from "@shared/store/actions";
import history from "@shared/history";
import { tokenHandler } from "@shared/utils";
import { ActionWithPayload, WithNavigate } from "@shared/interfaces";
import { NameOfChildRoutes, NamesOfParentRoutes } from "@shared/constants";

import { logout, login, registration, setPassword, forgotPassword, verify } from "./actions";
import api from "../api";
import { AuthActionTypes } from "./constants";
import {
  LoginFormShape,
  LoginSuccess,
  ForgotPasswordFormShape,
  ForgotPasswordSuccess,
  SetPasswordFormShape,
  VerifySuccess,
  VerifyShape,
} from "../interfaces";

function* logoutSaga() {
  yield tokenHandler.removeAll();
  yield put(logout.success());
  yield put(getUserDetails.success(null));
  yield history.push(NamesOfParentRoutes.AUTH);
}

function* verifySaga({ payload }: ActionWithPayload<WithNavigate<VerifyShape>>) {
  try {
    yield put(startLoading());
    yield tokenHandler.removeAll();

    const response: VerifySuccess = yield call(api.verify, payload);
    const { token } = response;

    if (token) {
      yield tokenHandler.set(token);
      yield put(getUserDetails.request());
    }

    yield put(verify.success(response));
    yield put(stopLoading());

    yield history.push("/");
  } catch (error) {
    yield put(verify.failure(error as Error));
    yield put(stopLoading());
  }
}

function* loginSaga({ payload }: ActionWithPayload<WithNavigate<LoginFormShape>>) {
  try {
    yield put(startLoading());
    yield tokenHandler.removeAll();

    const response: LoginSuccess = yield call(api.login, payload);

    if (response.set_password_token) {
      yield tokenHandler.setSPToken(response.set_password_token);
    }

    yield put(login.success(response));
    yield put(stopLoading());
    if (response.set_password_token) {
      payload.navigate(`${NamesOfParentRoutes.AUTH}${NameOfChildRoutes.AUTH.SET_PASSWORD}`, {
        state: {
          login: payload.login,
        },
      });
    } else {
      payload.navigate(`${NamesOfParentRoutes.AUTH}${NameOfChildRoutes.AUTH.VERIFY_USER}`, {
        state: {
          credentials: {
            login: payload.login,
            password: payload.password,
          },
        },
      });
    }
  } catch (error) {
    yield put(login.failure(error as Error));
    yield put(stopLoading());
  }
}

function* forgotPasswordSaga({ payload }: ActionWithPayload<ForgotPasswordFormShape>) {
  try {
    yield put(startLoading());
    yield tokenHandler.removeAll();
    const response: ForgotPasswordSuccess = yield call(api.forgotPassword, payload);
    yield put(forgotPassword.success(response));
    yield put(stopLoading());
  } catch (error) {
    yield put(forgotPassword.failure(error as Error));
    yield put(stopLoading());
  }
}

function* setPasswordSaga({ payload }: ActionWithPayload<WithNavigate<SetPasswordFormShape>>) {
  const { password } = payload;
  try {
    yield put(startLoading());
    yield tokenHandler.remove();

    const hash: string = yield tokenHandler.getSPToken();
    const response: LoginSuccess = yield call(api.setPassword, { password, hash });

    yield put(login.success(response));
    yield put(stopLoading());
    payload.navigate(`${NamesOfParentRoutes.AUTH}${NameOfChildRoutes.AUTH.VERIFY_USER}`, {
      state: {
        credentials: {
          login: payload.login,
          password: payload.password,
        },
      },
    });
  } catch (error) {
    yield put(setPassword.failure(error as Error));
    yield put(stopLoading());
  }
}

function* registrationSaga({ payload }: AnyAction) {
  try {
    yield put(startLoading());
    const { token } = yield call(api.registration, payload);
    yield tokenHandler.set(token);
    yield put(getUserDetails.request());
    yield put(registration.success());
    yield put(stopLoading());
    yield history.push("/");
  } catch (error) {
    yield put(registration.failure(error as Error));
    yield put(stopLoading());
  }
}

function* authSagas() {
  yield takeLatest(AuthActionTypes.LOGOUT, logoutSaga);
  yield takeLatest(AuthActionTypes.LOGIN, loginSaga);
  yield takeLatest(AuthActionTypes.VERIFY_USER, verifySaga);
  yield takeLatest(AuthActionTypes.FORGOT_PASSWORD, forgotPasswordSaga);
  yield takeLatest(AuthActionTypes.SET_PASSWORD, setPasswordSaga);
  yield takeLatest(AuthActionTypes.REGISTRATION, registrationSaga);
}

export default authSagas;
